import { useState, useEffect } from "react";
import { Truck, Loader2, MapPin, User, Building2, PackageCheck } from "lucide-react";
import { toast } from "sonner";
import api from "../../../lib/api";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../ui/dialog";
import { DispoBackButton } from "./DispoBackButton";
import { FeatureGuideTriangle } from "./FeatureGuideTriangle";

// ---------- Transportauftrag --------------------------------------------------
const STATUS_LABELS = { ordered: "Beauftragt", pickup: "Abholung", in_transit: "Unterwegs", delivered: "Geliefert", cancelled: "Storniert" };

const fmtSite = (s) => [s.name, [s.street, [s.zip, s.city].filter(Boolean).join(" ")].filter(Boolean).join(", ")].filter(Boolean).join(" — ");
const fmtCustomer = (c) => {
    if (!c) return "";
    const name = [c.first_name, c.last_name].filter(Boolean).join(" ");
    const addr = [c.street, [c.zip || c.postal_code, c.city].filter(Boolean).join(" ")].filter(Boolean).join(", ");
    return [name, addr].filter(Boolean).join(" — ");
};

const Radio = ({ checked, onChange, children, testid }) => (
    <label className={`flex items-center gap-2 px-3 py-2 rounded-sm border cursor-pointer text-sm ${checked ? "border-[#111827] bg-gray-50 font-bold" : "border-gray-300"}`} data-testid={testid}>
        <input type="radio" checked={checked} onChange={onChange} className="w-4 h-4 accent-[#111827]" />
        {children}
    </label>
);

export function TransportOrderModal({ listingId, existing, customer, transportMode = null, listingObj = null, onBack, onClose, onSaved }) {
    const [form, setForm] = useState({
        pickup_address: existing?.pickup_address || "",
        dropoff_address: existing?.dropoff_address || "",
        pickup_date: existing?.pickup_date || "",
        dropoff_date: existing?.dropoff_date || "",
        tracking_number: existing?.tracking_number || "",
        note: existing?.note || "",
    });
    const [saving, setSaving] = useState(false);
    const [statusUpdate, setStatusUpdate] = useState(existing?.status || "ordered");
    const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    // Auswahl-Logik (nur Erstellen)
    const [sites, setSites] = useState([]);
    const [partners, setPartners] = useState([]);
    const [fromSite, setFromSite] = useState("");
    const [toType, setToType] = useState("customer");     // customer | site
    const [toSite, setToSite] = useState("");
    const [carrierOrg, setCarrierOrg] = useState("dealer"); // customer | dealer
    const [dealerCarrier, setDealerCarrier] = useState("self"); // self | spediteur
    const [partnerId, setPartnerId] = useState("");
    const [newProvider, setNewProvider] = useState("");
    const [portalName, setPortalName] = useState("");
    const [portalUrl, setPortalUrl] = useState("");

    // Standorte + Dienstleister laden (nur Erstellen)
    useEffect(() => {
        if (existing) return;
        api.get("/users/me/dealer-sites").then((r) => {
            const items = r.data?.items || [];
            setSites(items);
            const def = items.find((s) => s.is_default) || items[0];
            if (def) { setFromSite(def.id || def.code); setForm((p) => ({ ...p, pickup_address: fmtSite(def) })); }
            if (items[0]) setToSite(items[0].id || items[0].code);
        }).catch(() => {});
        api.get("/dealers/me/partners?kind=dienstleister").then((r) => setPartners(r.data?.items || [])).catch(() => {});
    }, [existing]);

    // „Von" bei Standortwechsel aktualisieren
    useEffect(() => {
        if (existing || !fromSite) return;
        const s = sites.find((x) => (x.id || x.code) === fromSite);
        if (s) update("pickup_address", fmtSite(s));
    }, [fromSite, sites, existing]);

    // „Nach" abhängig von Auswahl aktualisieren
    useEffect(() => {
        if (existing) return;
        if (toType === "customer") {
            update("dropoff_address", fmtCustomer(customer));
        } else {
            const s = sites.find((x) => (x.id || x.code) === toSite);
            if (s) update("dropoff_address", fmtSite(s));
        }
    }, [toType, toSite, sites, customer, existing]);

    const resolveProvider = () => {
        if (carrierOrg === "customer") return { provider: "Selbstabholer / Kunde organisiert", mode: "internal" };
        if (dealerCarrier === "self") return { provider: "Eigener Fahrer (Händler)", mode: "internal" };
        if (dealerCarrier === "portal") return { provider: portalName.trim() ? `Portal: ${portalName.trim()}` : "", mode: "internal" };
        const p = partners.find((x) => x.id === partnerId);
        return { provider: p ? p.name : newProvider.trim(), mode: "external" };
    };

    const submit = async () => {
        if (existing?.id) {
            setSaving(true);
            try {
                await api.patch(`/listings/${listingId}/dispo/transport-order`, { status: statusUpdate, tracking_number: form.tracking_number });
                toast.success(`Transport-Status: ${STATUS_LABELS[statusUpdate]}`);
                onSaved?.(); onClose();
            } catch (e) { toast.error(e.response?.data?.detail || "Auftrag fehlgeschlagen"); }
            finally { setSaving(false); }
            return;
        }
        const { provider, mode } = resolveProvider();
        if (!provider) { toast.error(dealerCarrier === "spediteur" ? "Bitte Spediteur wählen oder eingeben." : dealerCarrier === "portal" ? "Bitte Portal-Namen eingeben." : "Transporteur fehlt."); return; }
        if (toType === "customer" && !customer) { toast.error("Keine Kundendaten hinterlegt — bitte zuerst Kundendaten erfassen."); return; }
        setSaving(true);
        try {
            await api.post(`/listings/${listingId}/dispo/transport-order`, {
                ...form, provider, mode,
                to_type: toType,
                carrier_org: carrierOrg,
                dealer_carrier: carrierOrg === "dealer" ? dealerCarrier : null,
                note: [form.note, dealerCarrier === "portal" && portalUrl.trim() ? `Portal-URL: ${portalUrl.trim()}` : "", transportMode ? `Transport-Dienstleistung: ${transportMode === "haendlernetz" ? "Händlernetzpauschale" : "Haustürlieferung Bundesgebiet"}` : ""].filter(Boolean).join(" · "),
            });
            toast.success(mode === "external" ? "Externer Transportauftrag erteilt (MOCKED Provider)" : "Interner Transportauftrag angelegt");
            onSaved?.(); onClose();
        } catch (e) { toast.error(e.response?.data?.detail || "Auftrag fehlgeschlagen"); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent onPointerDownOutside={(e) => e.preventDefault()} onInteractOutside={(e) => e.preventDefault()} className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dispo-modal-transport">
                <FeatureGuideTriangle guideKey="transport" />
                <DialogHeader><DialogTitle className="flex items-center gap-2"><Truck className="w-5 h-5" /> Transportauftrag {existing ? "verfolgen" : "erteilen"}</DialogTitle></DialogHeader>

                {existing ? (
                    <div className="space-y-3" data-testid="dispo-trans-existing">
                        <div className="text-xs text-gray-600">Aktueller Status: <span className="font-bold">{STATUS_LABELS[existing.status]}</span> · Provider: <span className="font-bold">{existing.provider}</span> · Mode: <span className="font-mono-num text-[10px] uppercase">{existing.mode}</span>{existing.external_provider_mocked && <span className="ml-2 bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded-sm font-bold uppercase tracking-wider text-[9px]">MOCKED</span>}</div>
                        {existing.external_provider_mocked && (
                            <div className="bg-amber-50 border border-amber-200 rounded-sm p-2 text-[11px] text-amber-900" data-testid="dispo-trans-mock-warning">
                                <strong>MOCKED:</strong> Externe Spediteur-API ist noch nicht angebunden — Statusupdates werden nur intern persistiert.
                            </div>
                        )}
                        <div>
                            <Label className="text-xs">Status aktualisieren</Label>
                            <select value={statusUpdate} onChange={(e) => setStatusUpdate(e.target.value)} className="w-full border border-gray-300 rounded-sm px-2 py-1.5 text-sm" data-testid="dispo-trans-status-select">
                                {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                            </select>
                        </div>
                        <div>
                            <Label className="text-xs">Tracking-Nummer aktualisieren (optional)</Label>
                            <Input value={form.tracking_number} onChange={(e) => update("tracking_number", e.target.value)} className="font-mono-num" data-testid="dispo-trans-tracking-update" />
                        </div>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {transportMode && (
                            <div className="bg-orange-50 border border-orange-200 rounded-sm p-2.5 text-[12px] text-orange-900" data-testid="dispo-trans-mode-banner">
                                <div className="font-bold uppercase tracking-wider text-[10px] mb-1">
                                    {transportMode === "haendlernetz" ? "Händlernetzpauschale gewählt" : "Deutschlandweite Haustürlieferung gewählt"}
                                </div>
                                <div><b>Abholort (Fahrzeug-Standort):</b> {listingObj?.location || form.pickup_address || "—"}</div>
                                <div><b>Anlieferort:</b> {transportMode === "haendlernetz" ? `Verkaufsstandort — ${form.dropoff_address || "unten wählen"}` : `Kundenadresse — ${fmtCustomer(customer) || "Käuferdaten fehlen"}`}</div>
                            </div>
                        )}
                        {/* Von */}
                        <div>
                            <Label className="text-xs flex items-center gap-1"><MapPin className="w-3 h-3" /> Von (Standort)</Label>
                            {sites.length > 1 ? (
                                <select value={fromSite} onChange={(e) => setFromSite(e.target.value)} className="w-full border border-gray-300 rounded-sm px-2 py-1.5 text-sm" data-testid="dispo-trans-from-site">
                                    {sites.map((s) => <option key={s.id || s.code} value={s.id || s.code}>{fmtSite(s)}</option>)}
                                </select>
                            ) : (
                                <Input value={form.pickup_address} readOnly className="bg-gray-50" data-testid="dispo-trans-pickup-addr" />
                            )}
                        </div>

                        {/* Nach */}
                        <div>
                            <Label className="text-xs flex items-center gap-1"><MapPin className="w-3 h-3" /> Nach</Label>
                            <div className="grid grid-cols-2 gap-2 mt-1">
                                <Radio checked={toType === "customer"} onChange={() => setToType("customer")} testid="dispo-trans-to-customer"><User className="w-3.5 h-3.5" /> Zum Kunden</Radio>
                                <Radio checked={toType === "site"} onChange={() => setToType("site")} testid="dispo-trans-to-site"><Building2 className="w-3.5 h-3.5" /> Zu anderem Standort</Radio>
                            </div>
                            {toType === "site" && (
                                <select value={toSite} onChange={(e) => setToSite(e.target.value)} className="w-full mt-2 border border-gray-300 rounded-sm px-2 py-1.5 text-sm" data-testid="dispo-trans-to-site-select">
                                    {sites.map((s) => <option key={s.id || s.code} value={s.id || s.code}>{fmtSite(s)}</option>)}
                                </select>
                            )}
                            <Input value={form.dropoff_address} onChange={(e) => update("dropoff_address", e.target.value)} className="mt-2" placeholder={toType === "customer" && !customer ? "Keine Kundendaten hinterlegt" : "Zieladresse"} data-testid="dispo-trans-dropoff-addr" />
                        </div>

                        {/* Transporteur */}
                        <div>
                            <Label className="text-xs flex items-center gap-1"><Truck className="w-3 h-3" /> Transporteur</Label>
                            <div className="grid grid-cols-2 gap-2 mt-1">
                                <Radio checked={carrierOrg === "customer"} onChange={() => setCarrierOrg("customer")} testid="dispo-trans-carrier-customer">Lieferung organisiert Kunde</Radio>
                                <Radio checked={carrierOrg === "dealer"} onChange={() => setCarrierOrg("dealer")} testid="dispo-trans-carrier-dealer">Lieferung organisiert Händler</Radio>
                            </div>
                            {carrierOrg === "dealer" && (
                                <div className="mt-2 pl-3 border-l-2 border-gray-200 space-y-2">
                                    <div className="grid grid-cols-2 gap-2">
                                        <Radio checked={dealerCarrier === "self"} onChange={() => setDealerCarrier("self")} testid="dispo-trans-dealer-self">Durch Händler (eigener Fahrer)</Radio>
                                        <Radio checked={dealerCarrier === "spediteur"} onChange={() => setDealerCarrier("spediteur")} testid="dispo-trans-dealer-spediteur">Durch Partnerunternehmen / Spediteur</Radio>
                                        <Radio checked={dealerCarrier === "portal"} onChange={() => setDealerCarrier("portal")} testid="dispo-trans-dealer-portal">Über Transport-Portal (eigene Zugangsdaten)</Radio>
                                        <button type="button" onClick={() => window.open("/dealer/partner-companies", "_blank")}
                                            className="text-left text-xs border-2 border-dashed border-gray-300 rounded-sm px-2 py-1.5 text-gray-600 hover:border-orange-400 hover:text-orange-700"
                                            data-testid="dispo-trans-partner-search">
                                            🔍 Partnerunternehmen suchen / anlegen …
                                        </button>
                                    </div>
                                    {dealerCarrier === "portal" && (
                                        <div className="space-y-2" data-testid="dispo-trans-portal-fields">
                                            <Input value={portalName} onChange={(e) => setPortalName(e.target.value)} placeholder="Portal-Name (z. B. Shiply, uShip, CarShipper)" data-testid="dispo-trans-portal-name" />
                                            <Input value={portalUrl} onChange={(e) => setPortalUrl(e.target.value)} placeholder="Portal-URL (Login mit Ihren eigenen Zugangsdaten)" data-testid="dispo-trans-portal-url" />
                                            <div className="text-[11px] text-gray-500">Der Auftrag wird intern dokumentiert — die Beauftragung erfolgt im Portal mit Ihren eigenen Zugangsdaten.</div>
                                        </div>
                                    )}
                                    {dealerCarrier === "spediteur" && (
                                        <div className="space-y-2" data-testid="dispo-trans-spediteur-picker">
                                            <select value={partnerId} onChange={(e) => { setPartnerId(e.target.value); if (e.target.value) setNewProvider(""); }} className="w-full border border-gray-300 rounded-sm px-2 py-1.5 text-sm" data-testid="dispo-trans-partner-select">
                                                <option value="">— Registrierten Dienstleister wählen —</option>
                                                {partners.map((p) => (
                                                    <option key={p.id} value={p.id}>{p.source === "eigen" ? "★ " : ""}{p.name}{p.city ? ` · ${p.city}` : ""}{p.source === "eigen" ? " (eigener)" : ""}</option>
                                                ))}
                                            </select>
                                            <div className="text-[11px] text-gray-500">oder noch nicht registrierten Dienstleister eingeben:</div>
                                            <Input value={newProvider} onChange={(e) => { setNewProvider(e.target.value); if (e.target.value) setPartnerId(""); }} placeholder="z. B. Spedition Müller" data-testid="dispo-trans-new-provider" />
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Termine + Notiz */}
                        <div className="grid grid-cols-2 gap-3">
                            <div><Label className="text-xs">Abholdatum</Label><Input type="date" value={form.pickup_date} onChange={(e) => update("pickup_date", e.target.value)} data-testid="dispo-trans-pickup-date" /></div>
                            <div><Label className="text-xs">Lieferdatum</Label><Input type="date" value={form.dropoff_date} onChange={(e) => update("dropoff_date", e.target.value)} data-testid="dispo-trans-dropoff-date" /></div>
                        </div>
                        <div><Label className="text-xs">Notiz</Label><Textarea rows={2} value={form.note} onChange={(e) => update("note", e.target.value)} data-testid="dispo-trans-note" /></div>
                        {carrierOrg === "dealer" && dealerCarrier === "spediteur" && (
                            <div className="bg-amber-50 border border-amber-200 rounded-sm p-2 text-[11px] text-amber-900 flex items-start gap-1.5" data-testid="dispo-trans-mock-warning">
                                <PackageCheck className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                                <span><strong>MOCKED:</strong> Externe Spediteur-API ist noch nicht angebunden. Der Auftrag wird intern gespeichert und beim Live-Setup an die echte API gepusht.</span>
                            </div>
                        )}
                    </div>
                )}

                <DialogFooter>
                    <DispoBackButton onBack={onBack} />
                    <Button variant="outline" onClick={onClose}>Abbrechen</Button>
                    <Button onClick={submit} disabled={saving} data-testid="dispo-trans-submit">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (existing ? "Status aktualisieren" : "Transportauftrag erteilen")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
